import { Box, Divider, Menu, MenuItem, Theme, useMediaQuery } from '@mui/material';
import { palette } from '@/theme/constants';
import { useTranslation } from 'next-i18next';
import { templatesSelector } from '@/store/selectors';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { MuiIconButton } from '../IconButton';
import { useSelector } from 'react-redux';

interface ViewedControlsDropdownProps {
  extendedPalette?: any;
  top?: any;
  onMarkAllViewed: () => void;
  onMarkAllUnviewed: () => void;
}

export const ViewedControlsDropdown = ({
  extendedPalette,
  top,
  onMarkAllViewed,
  onMarkAllUnviewed,
}: ViewedControlsDropdownProps) => {
  const { t } = useTranslation();
  const [adminPalette, setAdminPalette] = useState({
    storyBackgroundColor: '#333333',
    textColor: '#fff',
    accentColor: '#BF5700',
  });

  const router = useRouter();
  const { template } = useSelector(templatesSelector);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (template?.template?.colors) {
      const colors = template.template.colors.reduce((acc: any, color: any) => {
        switch (color.PLabel) {
          case 'storyBackground':
            acc.storyBackgroundColor = color.PValue;
            break;
          case 'TextColor':
            acc.textColor = color.PValue;
            break;
          case 'AccentColor':
            acc.accentColor = color.PValue;
            break;
          default:
            break;
        }
        return acc;
      }, {});

      setAdminPalette({
        storyBackgroundColor: colors.storyBackgroundColor || '#333333',
        textColor: colors.textColor || '#fff',
        accentColor: colors.accentColor || '#BF5700',
      });
    }
  }, [template]);

  const controlOptions = [
    { id: 'mark-all-viewed', name: 'Mark all as viewed', action: onMarkAllViewed },
    { id: 'mark-all-unviewed', name: 'Mark all as unviewed', action: onMarkAllUnviewed },
  ];

  const handleMenuItemClick = (option: typeof controlOptions[0]) => {
    option.action();
    setAnchorEl(null);
  };

  return (
    <div>
      <Box onClick={handleClickMenu} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <MuiIconButton
          icon='/icons/undo'
          label={!isMobile ? 'Viewed' : 'Viewed'}
          altIcon='reset'
          iconPosition='left'
          direction='row'
          background={!isMobile ? (extendedPalette?.hasCustomTheme ? `${extendedPalette.buttonbackgroundIcon} !important` : '#212d6d !important') : 'transparent'}
          width={!isMobile ? 120 : 70}
          isRounded={false}
          height={40}
          iconHeight={10}
          iconWidth={14}
          sx={{
            borderRadius: '24px',
            fontSize: '14px',
            '&:hover': {
              backgroundColor: `${extendedPalette?.hasCustomTheme ? extendedPalette.buttonbackgroundIcon : '#0072CE'} !important`,
              borderColor: `${extendedPalette?.hasCustomTheme ? extendedPalette.buttonbackgroundIcon : '#0072CE'}`,
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#D8E8F6',
            borderRadius: '12px',
            width: '200px',
            padding: '8px',
            top: '1rem',
            left: {
              xs: '0',
              sm: '0',
              md: '0',
              lg: '72% !important',
              xl: '0%',
            },
          },
        }}>
        {controlOptions.map((option, index) => (
          <div key={option.id}>
            <MenuItem
              onClick={() => handleMenuItemClick(option)}
              sx={{
                justifyContent: 'space-between',
                fontSize: '16px',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: '#0072CE',
                  color: '#fff',
                },
              }}>
              {option.name}
            </MenuItem>
            {index !== controlOptions.length - 1 && <Divider />}
            {index < controlOptions.length && <hr style={{ backgroundColor: '#C6D7E7', border: 'none', margin: '0' }} />}
          </div>
        ))}
      </Menu>
    </div>
  );
};
